import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import Button from '../core/Button';
import Input from '../core/Input';
import Tooltip from '../Tooltip';
import { cn } from '../../helpers/classnames';
import {
  IconArrowLeft,
  IconArrowRight,
  IconSave,
  IconTrash,
} from '../../icons';
import PeriodicityControl from './PeriodicityControl';
import CheckInDnDContainer from './CheckInDnDContainer';
import MapBase from '../Map';
import { Services, Statics } from '../Map/MapBase';
import { useContractors } from '../../hooks/useContractors';
import Permissions from '../auth/Permissions';
import { EntitySearch } from '../EntitySearch';
import mapStore from '../../store/map';
import useConfig from '../../store/config';
import { Dropdown, OptionsType } from '../core/Dropdown';
import { syncRoutePlanWithEntities } from '../../helpers/check_in_plan_helpers';

const messages = defineMessages({
  choose: {
    id: 'choose',
    defaultMessage: 'Choose...',
  },
});

interface EditCheckInPlanFormProps {
  resolvedPlan: CheckInPlanResolved;
  entities: ListAndLookup<Entity>;
  isProcessing: boolean;
  onRemovePlan?: () => void;
  onBack: () => void;
  onChange: (updatedPlan: Partial<CheckInPlan>) => void;
  onSave: (clearRoutePlan?: boolean) => void;
}

const maxWayPoints = 20;
const minimumTimeBetweenRequests = 3000;

const EditCheckInPlanForm: FC<EditCheckInPlanFormProps> = ({
  resolvedPlan,
  isProcessing,
  onRemovePlan,
  onBack,
  onChange,
  onSave,
  entities,
}) => {
  const { mapDefaultCenter } = useConfig();
  const { list: contractors } = useContractors();
  const [showRoutePlanning, setShowRoutePlanning] = useState<boolean>(false);
  const { setMap, mapById } = mapStore();
  const routeRequestTimeout = useRef<{
    requestId: null | string;
    timer: null | NodeJS.Timeout;
    lastRequest: number;
    mounted: boolean;
    mapStatics: Statics | null;
  }>({
    requestId: null,
    timer: null,
    lastRequest: 0,
    mounted: true,
    mapStatics: null,
  });
  const intl = useIntl();

  useEffect(() => {
    const timeouts = routeRequestTimeout.current;
    timeouts.mounted = true;
    return () => {
      timeouts.mounted = false;
      timeouts.mapStatics?.renderer &&
        (timeouts.mapStatics.renderer as google.maps.DirectionsRenderer).setMap(
          null,
        );
    };
  }, []);

  useEffect(() => {
    if (mapById['checkInRoutePlanner']) {
      return;
    }
    setMap({
      id: 'checkInRoutePlanner',
      center: mapDefaultCenter,
      markers: {},
      zoom: 14,
    });
  }, [mapById, mapDefaultCenter, setMap]);

  const isValid = useMemo(() => {
    return Object.keys(resolvedPlan.invalid_inputs).length === 0;
  }, [resolvedPlan.invalid_inputs]);
  const onDoUpdate = useCallback(
    (key: string, val: number | string | string[] | undefined) => {
      const update = { [key]: val };
      onChange(update);
    },
    [onChange],
  );
  const onPeriodicityChange = useCallback(
    (periodicity: Periodicity) => {
      onChange({
        periodicity,
      });
    },
    [onChange],
  );

  const routePlan = useMemo(() => {
    return resolvedPlan.route_plan_entities.length > 0
      ? resolvedPlan.route_plan_entities
      : resolvedPlan.entities;
  }, [resolvedPlan.entities, resolvedPlan.route_plan_entities]);

  const contractorOptions = useMemo<OptionsType[]>(() => {
    return contractors.map(({ id, name }) => ({
      id,
      label: name,
    }));
  }, [contractors]);

  const onRouteRequest = useCallback(
    (map: google.maps.Map, services: Services, statics: Statics) => {
      routeRequestTimeout.current.mapStatics = statics;
      if (!services.directions || routePlan.length < 2) {
        return;
      }
      const routeId = routePlan.map((e) => e.id).join('_');
      if (statics.currentRouteId === routeId) {
        return;
      }
      statics.currentRouteId = routeId;

      if (!statics.renderer) {
        const newRenderer = new google.maps.DirectionsRenderer();
        newRenderer.setMap(map);
        statics.renderer = newRenderer;
      }
      const renderer = statics.renderer as google.maps.DirectionsRenderer;
      //Create route request.
      const routeCoordsClone = [...resolvedPlan.route_plan_coords];
      const first = routeCoordsClone.shift();
      const last = routeCoordsClone.pop();
      if (!first || !last) {
        return;
      }
      const routeRequest: google.maps.DirectionsRequest = {
        origin: first,
        destination: last,
        travelMode: google.maps.TravelMode.DRIVING,
      };
      if (routeCoordsClone.length > 0) {
        routeRequest.waypoints = routeCoordsClone
          .slice(0, maxWayPoints - 1)
          .map((point) => ({
            stopover: true,
            location: new google.maps.LatLng(point.lat, point.lng),
          }));
      }
      const timeouts = routeRequestTimeout.current;
      const request = (): void => {
        if (!timeouts.mounted) {
          return;
        }
        timeouts.lastRequest = Date.now().valueOf();
        services.directions.route(routeRequest, (results) => {
          if (!timeouts.mounted) {
            return;
          }
          renderer.setDirections(results);
        });
      };

      const timeSinceLastRequest = Date.now().valueOf() - timeouts.lastRequest;
      if (timeSinceLastRequest < minimumTimeBetweenRequests) {
        //Too early, we need to wait to do the request.
        if (timeouts.timer) {
          clearTimeout(timeouts.timer);
          timeouts.timer = null;
        }
        timeouts.timer = setTimeout(
          request,
          minimumTimeBetweenRequests - timeSinceLastRequest,
        );
      } else {
        //Free to do a request.
        request();
        timeouts.lastRequest = Date.now().valueOf();
        //If there is a timer clean it up.
        if (timeouts.timer) {
          clearTimeout(timeouts.timer);
          timeouts.timer = null;
        }
      }
    },
    [resolvedPlan, routePlan],
  );

  const onReorderRoutePlan = useCallback(
    (reorderedPlan: Entity[]) => {
      onChange({ route_plan: reorderedPlan.map((e) => e.id) });
    },
    [onChange],
  );

  const onStopSearchChange = useCallback(
    (ids: string[]) => {
      onChange({
        entities: ids,
        route_plan: syncRoutePlanWithEntities(ids, resolvedPlan.route_plan),
      });
    },
    [onChange, resolvedPlan.route_plan],
  );

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-5">
        <div className="flex flex-row gap-12 pt-2">
          <div
            className={cn(
              'flex h-[500px] grow flex-row gap-2',
              showRoutePlanning ? 'visible static' : 'invisible absolute',
            )}
          >
            <div className="grow basis-0">
              <p className="ml-5 text-[10px] uppercase leading-4 tracking-[1.5px]">
                <FormattedMessage
                  id="check_in_plan_management.stops"
                  defaultMessage="Stops"
                />
              </p>
              <CheckInDnDContainer
                entities={routePlan}
                onReorder={onReorderRoutePlan}
              />
            </div>
            <div className="h-full min-h-[500px] grow basis-0">
              <MapBase
                mapId="checkInRoutePlanner"
                paused={!showRoutePlanning}
                onMarkerUpdate={(map, services, markers, statics) => {
                  onRouteRequest(map, services, statics);
                }}
              />
            </div>
          </div>
          {!showRoutePlanning && (
            <>
              <div className="h-[500px] grow basis-0">
                <p className="text-[10px] uppercase leading-4 tracking-[1.5px]">
                  <FormattedMessage
                    id="check_in_plan_management.title_input_label"
                    defaultMessage="Title"
                  />
                </p>
                <div className="mb-2">
                  <Input
                    type="text"
                    value={resolvedPlan.title || ''}
                    onChange={(ev) => onDoUpdate('title', ev.target.value)}
                  />
                </div>
                <Permissions has="role:admin">
                  <p className="text-[10px] uppercase leading-4 tracking-[1.5px]">
                    <FormattedMessage
                      id="check_in_plan_management.contractor_input_label"
                      defaultMessage="Contractor"
                    />
                  </p>
                  <Dropdown
                    id="contractor-dropdown"
                    options={contractorOptions}
                    placeholder={intl.formatMessage(messages.choose)}
                    value={
                      contractorOptions.find(
                        (c) => c.id === resolvedPlan.contractor_id,
                      ) || undefined
                    }
                    onValueChange={(value) => {
                      onDoUpdate('contractor_id', value.id);
                    }}
                    preventReselect={true}
                    className="mb-2"
                  />
                </Permissions>
                <p className="text-[10px] uppercase leading-4 tracking-[1.5px]">
                  <FormattedMessage
                    id="check_in_plan_management.duration_input_label"
                    defaultMessage="Duration"
                  />
                </p>
                <Tooltip
                  side="top"
                  label={
                    <FormattedMessage
                      id="check_in_plan_management.duration_input_tooltip"
                      defaultMessage="The number of days before occurrence the check-in will be visible in the HållKoll contractor web application."
                    />
                  }
                >
                  <div className="mb-2">
                    <Input
                      type="number"
                      value={resolvedPlan.duration || ''}
                      onChange={(ev) => {
                        const v = ev.target.value
                          ? parseInt(ev.target.value, 10)
                          : undefined;
                        if (!v || !isFinite(v) || v < 0) {
                          onDoUpdate('duration', undefined);
                          return;
                        }
                        onDoUpdate('duration', v);
                      }}
                    />
                  </div>
                </Tooltip>
                <PeriodicityControl
                  onChange={onPeriodicityChange}
                  periodicity={resolvedPlan.periodicity}
                />
              </div>
              <div className="grow basis-0">
                <p className="text-[10px] uppercase leading-4 tracking-[1.5px]">
                  <FormattedMessage
                    id="check_in_plan_management.stops"
                    defaultMessage="Stops"
                  />
                </p>
                <EntitySearch
                  selectedEntityIds={resolvedPlan.entity_ids}
                  onChange={onStopSearchChange}
                  options={entities.list}
                  hideLabel={true}
                  className="max-h-[500px]"
                />
              </div>
            </>
          )}
        </div>
      </div>
      <div className="shrink-0 p-2">
        <div className="flex w-full flex-row items-center gap-2">
          {showRoutePlanning ? (
            <Button
              variant="ghost"
              startIcon={<IconArrowLeft />}
              onClick={() => setShowRoutePlanning(false)}
            >
              <FormattedMessage
                id="check_in_plan_management.to_plan_details"
                defaultMessage="Back to check-in plan details"
              />
            </Button>
          ) : (
            <Button
              variant="ghost"
              startIcon={<IconArrowLeft />}
              disabled={isProcessing}
              onClick={onBack}
            >
              <FormattedMessage
                id="check_in_plan_management.back_button_label"
                defaultMessage="Back to existing plans"
              />
            </Button>
          )}
          {onRemovePlan ? (
            <div className="flex grow justify-center">
              <Tooltip
                side="top"
                label={
                  <FormattedMessage
                    id="check_in_plan_management.remove_tooltip"
                    defaultMessage="Deletes the selected check-in plan."
                  />
                }
              >
                <Button
                  variant="outline"
                  color="red"
                  loading={isProcessing}
                  onClick={onRemovePlan}
                  endIcon={<IconTrash />}
                >
                  <FormattedMessage id="remove" defaultMessage="Remove" />
                </Button>
              </Tooltip>
            </div>
          ) : (
            <div className="grow" />
          )}
          {!showRoutePlanning && (
            <Tooltip
              side="top"
              label={
                isValid ? (
                  <FormattedMessage
                    id="check_in_plan_management.skip_route_planning_tooltip"
                    defaultMessage="Save without route planning. Route planning can be updated later."
                  />
                ) : (
                  <span className="block whitespace-pre-wrap">
                    <FormattedMessage
                      id="check_in_plan_management.skip_route_planning_disabled_tooltip"
                      defaultMessage="Save without route planning. Route planning can be updated later. Disabled because some details are missing."
                    />
                    {resolvedPlan.invalid_inputs.duration &&
                      `\n- ` + resolvedPlan.invalid_inputs.duration}
                    {resolvedPlan.invalid_inputs.entities &&
                      `\n- ` + resolvedPlan.invalid_inputs.entities}
                    {resolvedPlan.invalid_inputs.contractor &&
                      `\n- ` + resolvedPlan.invalid_inputs.contractor}
                    {resolvedPlan.invalid_inputs.periodicity &&
                      `\n- ` + resolvedPlan.invalid_inputs.periodicity}
                    {resolvedPlan.invalid_inputs.title &&
                      `\n- ` + resolvedPlan.invalid_inputs.title}
                  </span>
                )
              }
            >
              <span className="inline-flex">
                <Button
                  variant="ghost"
                  loading={isProcessing}
                  disabled={!isValid}
                  onClick={() => onSave(true)}
                >
                  <FormattedMessage
                    id="check_in_plan_management.skip_route_planning"
                    defaultMessage="Skip route planning"
                  />
                </Button>
              </span>
            </Tooltip>
          )}
          {!showRoutePlanning && (
            <Tooltip
              side="top"
              label={
                <FormattedMessage
                  id="check_in_plan_management.to_route_planning_tooltip"
                  defaultMessage="Go to route planning"
                />
              }
            >
              <Button
                variant="filled"
                loading={isProcessing}
                onClick={() => setShowRoutePlanning(true)}
                endIcon={<IconArrowRight />}
              >
                <FormattedMessage
                  id="check_in_plan_management.to_route_planning"
                  defaultMessage="To route planning"
                />
              </Button>
            </Tooltip>
          )}

          {showRoutePlanning && onRemovePlan && (
            <Tooltip
              side="top"
              label={
                isValid ? (
                  <FormattedMessage
                    id="check_in_plan_management.save_tooltip"
                    defaultMessage="Save check-in plan"
                  />
                ) : (
                  <span className="block whitespace-pre-wrap">
                    <FormattedMessage
                      id="check_in_plan_management.save_disabled_tooltip"
                      defaultMessage="Save check-in plan. Disabled because some details are missing or incorrect."
                    />
                    {resolvedPlan.invalid_inputs.duration &&
                      `\n- ` + resolvedPlan.invalid_inputs.duration}
                    {resolvedPlan.invalid_inputs.entities &&
                      `\n- ` + resolvedPlan.invalid_inputs.entities}
                    {resolvedPlan.invalid_inputs.periodicity &&
                      `\n- ` + resolvedPlan.invalid_inputs.periodicity}
                    {resolvedPlan.invalid_inputs.title &&
                      `\n- ` + resolvedPlan.invalid_inputs.title}
                  </span>
                )
              }
            >
              <span className="inline-flex">
                <Button
                  variant="filled"
                  loading={isProcessing}
                  disabled={!isValid}
                  onClick={() => onSave()}
                  startIcon={<IconSave />}
                >
                  <FormattedMessage
                    id="check_in_plan_management.save_check_in_plan"
                    defaultMessage="Save check-in plan"
                  />
                </Button>
              </span>
            </Tooltip>
          )}
          {showRoutePlanning && !onRemovePlan && (
            <Tooltip
              side="top"
              label={
                isValid ? (
                  <FormattedMessage
                    id="check_in_plan_management.create_tooltip"
                    defaultMessage="Create new check-in plan"
                  />
                ) : (
                  <span className="block whitespace-pre-wrap">
                    <FormattedMessage
                      id="check_in_plan_management.create_disabled_tooltip"
                      defaultMessage="Create check-in plan. Disabled because some details are missing or incorrect:"
                    />
                    {resolvedPlan.invalid_inputs.duration &&
                      `\n- ` + resolvedPlan.invalid_inputs.duration}
                    {resolvedPlan.invalid_inputs.entities &&
                      `\n- ` + resolvedPlan.invalid_inputs.entities}
                    {resolvedPlan.invalid_inputs.periodicity &&
                      `\n- ` + resolvedPlan.invalid_inputs.periodicity}
                    {resolvedPlan.invalid_inputs.title &&
                      `\n- ` + resolvedPlan.invalid_inputs.title}
                  </span>
                )
              }
            >
              <span className="inline-flex">
                <Button
                  variant="filled"
                  loading={isProcessing}
                  disabled={!isValid}
                  onClick={() => onSave()}
                  startIcon={<IconSave />}
                >
                  <FormattedMessage
                    id="check_in_plan_management.create_check_in_plan"
                    defaultMessage="Create new check-in plan"
                  />
                </Button>
              </span>
            </Tooltip>
          )}
        </div>
      </div>
    </>
  );
};

export default EditCheckInPlanForm;
